/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Models */
import { DsMap } from '../models/ds-map.class';
/** Interfaces */
import { DsRenameMapInterface } from '../interfaces/ds-rename-map.interface';
import { DsMapInterface } from '../interfaces/ds-map.interface';
import { DsMapFilters } from '../models/ds-map-filters.class';
/** Functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';
import { DS_MAPS_BASE_URL } from '../constants/ds-maps-base-url.constant';

@Injectable({
  providedIn: 'root',
})
/**
 * Service for 2D maps.
 */
export class DsMapsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get 2D maps and their categories.
   *
   * @param projectId The project id.
   * @param elementsPerPage the number of elements per page
   * @param filters Map filters
   * @param pageIndex the page index
   * @returns Observable<BydModel<DsMap>>
   */
  public getMaps(
    projectId: number,
    filters: DsMapFilters,
    elementsPerPage = 10,
    pageIndex = 0
  ): Observable<BydModel<DsMap>> {
    this.path = `${DS_MAPS_BASE_URL}${projectId}/maps/2D`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams<DsMapFilters>(filters);
    return this.getModels<DsMap>({ headers, params });
  }

  /**
   * Returns the selected map.
   *
   * @param projectId The project id
   * @param mapId The map id
   * @returns Observable<DsMap>
   */
  public getMap(projectId: number, mapId: number): Observable<DsMap> {
    this.path = `${DS_MAPS_BASE_URL}${projectId}/maps/2D/${mapId}`;
    return this.getModel<DsMap>();
  }

  /**
   * Get detailed 2D maps, their categories, whether they can be deleted.
   *
   * @param projectId The project id
   * @returns Observable<BydModel<DsMap>>
   */
  public getDetailedMaps(projectId: number): Observable<BydModel<DsMap>> {
    this.path = `/plans/2D/${projectId}`;
    return this.getModels<DsMap>();
  }

  /**
   * Add a 2D map.
   *
   * @param projectId The project id
   * @param data Form data containing map info
   * @returns Object
   */
  public addMap(projectId: number, data: FormData) {
    this.path = `${DS_MAPS_BASE_URL}${projectId}/maps/2D`;
    const headers = this.bydConfigService.headers.delete('Content-Type');
    return this.post({ body: data, headers });
  }

  /**
   * Renames a 2D map.
   *
   * @param projectId The project id
   * @param id Id of the 2D map
   * @param newName New name to set
   * @returns Observable<DsRenameMapInterface>
   */
  public renameMap(projectId: number, id: number, newName: string): Observable<DsRenameMapInterface> {
    this.path = `/plans/2D/${projectId}/rename/${id}`;
    return this.put<DsRenameMapInterface>({ body: { planName: newName } });
  }

  /**
   * Deletes a 2D map.
   *
   * @param projectId The project id
   * @param id Id of the 2D map
   * @returns Object
   */
  public deleteMap(projectId: number, id: number) {
    this.path = `/plans/2D/${projectId}/delete/${id}`;
    return this.delete();
  }

  /**
   * Create DsMap instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsMap` instance.
   */
  public createNewInstance(jsonObject: DsMapInterface): DsMap {
    return new DsMap(jsonObject);
  }
}
